"use client"

import { useCallback } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { useAuth } from "@/hooks/use-auth"

export interface TwilioIntegration {
  id: string
  tenant_id: string
  account_name?: string
  status: 'connected' | 'error' | 'disabled'
  last_validated_at?: string
  validation_error?: string
  has_credentials: boolean
  created_at: string
  updated_at: string
}

export interface PhoneNumber {
  sid: string
  phoneNumber: string
  friendlyName: string
  capabilities: {
    voice: boolean
    sms: boolean
    mms: boolean
    fax: boolean
  }
  voiceUrl?: string
  status: 'in-use' | 'available'
}

export const twilioIntegrationQueryKey = (tenantId: string) =>
  ["voice", tenantId, "twilio-integration"] as const
export const twilioPhoneNumbersQueryKey = (tenantId: string) =>
  ["voice", tenantId, "twilio-phone-numbers"] as const

export function useTwilioIntegration() {
  const { fetchWithAuth, isAuthenticated, user } = useAuth()
  const queryClient = useQueryClient()
  const tenantId =
    user?.impersonated_tenant_id ?? user?.tenant_id ?? "unauthenticated"
  const integrationKey = twilioIntegrationQueryKey(tenantId)
  const phoneNumbersKey = twilioPhoneNumbersQueryKey(tenantId)
  const integrationQuery = useQuery({
    queryKey: integrationKey,
    enabled: isAuthenticated,
    queryFn: async (): Promise<TwilioIntegration | null> => {
      const response = await fetchWithAuth("/api/voice/integrations/twilio")
      const data = (await response.json()) as {
        integration?: TwilioIntegration | null
        error?: string
      }
      if (!response.ok) {
        throw new Error(data.error ?? "Failed to load Twilio integration")
      }
      return data.integration ?? null
    },
  })
  const integration = integrationQuery.data ?? null

  const saveCredentialsMutation = useMutation({
    mutationFn: async ({
      accountSid,
      authToken,
    }: {
      accountSid: string
      authToken: string
    }): Promise<TwilioIntegration> => {
      const response = await fetchWithAuth("/api/voice/integrations/twilio", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          account_sid: accountSid,
          auth_token: authToken,
        }),
      })
      const data = (await response.json()) as {
        integration?: TwilioIntegration
        error?: string
      }
      if (!response.ok || !data.integration) {
        throw new Error(data.error ?? "Failed to save Twilio credentials")
      }
      return data.integration
    },
    onSuccess: (savedIntegration) => {
      queryClient.setQueryData(
        integrationKey,
        savedIntegration,
      )
    },
  })
  const saveCredentialsAsync = saveCredentialsMutation.mutateAsync
  const saveCredentials = useCallback(
    (accountSid: string, authToken: string) =>
      saveCredentialsAsync({ accountSid, authToken }),
    [saveCredentialsAsync],
  )

  const deleteIntegrationMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await fetchWithAuth("/api/voice/integrations/twilio", {
        method: "DELETE",
      })
      if (!response.ok) {
        const data = (await response.json()) as { error?: string }
        throw new Error(data.error ?? "Failed to delete Twilio integration")
      }
    },
    onSuccess: () => {
      queryClient.setQueryData(integrationKey, null)
      queryClient.removeQueries({ queryKey: phoneNumbersKey })
    },
  })

  const phoneNumbersQuery = useQuery({
    queryKey: phoneNumbersKey,
    enabled: false,
    queryFn: async (): Promise<PhoneNumber[]> => {
      if (!integration?.has_credentials) return []
      const response = await fetchWithAuth("/api/voice/integrations/twilio/numbers")
      const data = (await response.json()) as {
        numbers?: PhoneNumber[]
        error?: string
      }
      if (!response.ok) {
        throw new Error(data.error ?? "Failed to load phone numbers")
      }
      return data.numbers ?? []
    },
  })
  const phoneNumbers = phoneNumbersQuery.data ?? []
  const refetchPhoneNumbers = phoneNumbersQuery.refetch
  const loadPhoneNumbers = useCallback(
    async () => {
      const result = await refetchPhoneNumbers()
      if (result.error) throw result.error
      return result.data ?? []
    },
    [refetchPhoneNumbers],
  )
  const refetchIntegration = integrationQuery.refetch
  const refreshIntegration = useCallback(
    async () => {
      await refetchIntegration()
    },
    [refetchIntegration],
  )

  return {
    integration,
    phoneNumbers,
    isLoading: isAuthenticated ? integrationQuery.isPending : true,
    isLoadingNumbers: phoneNumbersQuery.isFetching,
    error:
      integrationQuery.error instanceof Error
        ? integrationQuery.error.message
        : null,
    isConfigured: !!integration?.has_credentials,
    saveCredentials,
    deleteIntegration: deleteIntegrationMutation.mutateAsync,
    loadPhoneNumbers,
    refreshIntegration,
  }
}
